import { createPortal } from "react-dom";
import { useEffect } from "react";
import "./RabbitMqAuthModal.scss";

type Props = {
  isOpen: boolean;
  canClose?: boolean;
  username: string;
  password: string;
  isSubmitting: boolean;
  errorMessage: string;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onClose: () => void;
};

const RabbitMqAuthModal = ({
  isOpen,
  canClose = true,
  username,
  password,
  isSubmitting,
  errorMessage,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
  onClose,
}: Props) => {
  useEffect(() => {
    if (!isOpen || !canClose) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canClose, isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="mqttLogAuthModalOverlay"
      role="dialog"
      aria-modal="true"
      aria-label="RabbitMQ authentication"
      onMouseDown={(event) => {
        if (canClose && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="mqttLogAuthModal">
        <div className="mqttLogAuthModalHeader">
          <div>
            <h2>RabbitMQ 認証情報を入力</h2>
          </div>
          {canClose ? (
            <button
              type="button"
              className="mqttLogAuthModalClose"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          ) : null}
        </div>

        <form className="mqttLogAuthModalBody" onSubmit={onSubmit}>
          <label className="mqttLogAuthModalField">
            <span>ユーザー名</span>
            <input
              type="text"
              value={username}
              onChange={(event) => onUsernameChange(event.target.value)}
              placeholder="ユーザー名"
              autoComplete="username"
            />
          </label>

          <label className="mqttLogAuthModalField">
            <span>パスワード</span>
            <input
              type="password"
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              placeholder="パスワード"
              autoComplete="current-password"
            />
          </label>

          {errorMessage ? (
            <div className="mqttLogAuthModalError">{errorMessage}</div>
          ) : null}

          <div className="mqttLogAuthModalFooter">
            {canClose ? (
              <button
                type="button"
                className="mqttLogAuthModalButton"
                onClick={onClose}
                disabled={isSubmitting}
              >
                閉じる
              </button>
            ) : null}
            <button
              type="submit"
              className="mqttLogAuthModalButton mqttLogAuthModalButtonPrimary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "認証中..." : "認証する"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default RabbitMqAuthModal;
